<script setup lang="ts">
import { sleep } from "@/modules/common";
import { useGlobalUser } from "@/stores/globalUser";
import { useInteractive } from "@/stores/interactive";
import { useUI } from "@/stores/ui";
import { MateHouseSessionMessage } from "@/types/entity";
import { MateSprite } from "@/types/game";
import { useElementSize, useWindowSize, watchDebounced } from "@vueuse/core";
import moment from "moment";
import { Game } from "phaser";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { FrameMode } from "../enums/uiEnums";
import StartGame from "../main";
import { HouseScene } from "../scenes/HouseScene";
import { useGameUI } from "../store/gameUI";
import { useHouseScene } from "../store/houseScene";
import { HouseSessionTab, useHouseSession } from "../store/houseSession";
import { EventBus } from "../utils/EventBus";
import CharacterDetailPopup from "./CharacterDetailPopup.vue";
import CharacterInvitePopup from "./CharacterInvitePopup.vue";
import HousePanel from "./panel/HousePanel.vue";
import GameUI from "./ui/GameUI.vue";
import { useGlobalModal } from "@/stores/globalModal";

const houseStore = useHouseSession();
const houseSceneStore = useHouseScene();
const userStore = useGlobalUser();
const gameUIStore = useGameUI();
const router = useRouter();
const interactiveStore = useInteractive();
const route = useRoute();
const modalStore = useGlobalModal();

const { isFreeMode: isControlMode } = storeToRefs(gameUIStore);
// Save the current scene instance
const scene = ref<HouseScene | null>(null);
const game = ref<Game | null>(null);
const containerRef = ref<HTMLElement>();
const { activeTab } = storeToRefs(houseStore);
const { showCharacterDetail } = storeToRefs(houseSceneStore);
const routeMeta = computed(() => route.meta);

// 响应式尺寸状态
const { width: containerWidth, height: containerHeight } =
  useElementSize(containerRef);

const emit = defineEmits(["current-active-scene"]);

// 初始化游戏
const initGame = async () => {
  await nextTick();

  await houseSceneStore.loadRoomJson();
  game.value = StartGame("game-container");
  game.value.scene.add("house", HouseScene, true);

  // FIXME: 为Event增加TS类型和枚举描述
  EventBus.on("current-scene-ready", (scene_instance: HouseScene) => {
    emit("current-active-scene", scene_instance);
    scene.value = scene_instance;
  });
  EventBus.on("close-character-detail-popup", () => {
    showCharacterDetail.value = false;
    targetInfo.value = null;
  });
  EventBus.on("item-click", (itemName: string) => {
    if (itemName === "bookshelf") {
      router.push({ name: "missionJourney" });
    } else if (itemName === "mail_box") {
      router.push({ name: "mailList" });
    } else if (itemName === "toolbox") {
      router.push({ name: "missionEquipment" });
    } else if (itemName === "traffic_sign") {
      router.push({ name: "missionHome" });
    }
  });
};

const loading = ref(true);
onMounted(async () => {
  loading.value = true;
  try {
    activeTab.value = (route.query.tab as HouseSessionTab) || "members";
    const promises = [
      userStore.refreshUser(),
      interactiveStore.getRelationshipList({ targetType: "mine" }),
      houseStore.getOrFetchHouseSession(
        userStore.user.currentMateHouseSession.uuid,
        true
      ),
      houseStore.initEventListIfAbsent(true),
      interactiveStore.getPoolList(),
    ];
    await Promise.all(promises);
  } finally {
    loading.value = false;
  }
  await nextTick();
  await initGame();
});

// 监听尺寸变化
watchDebounced(
  [containerWidth, containerHeight, () => gameUIStore.frameMode],
  async ([newWidth, newHeight]) => {
    if (game.value && (newWidth > 0 || newHeight > 0)) {
      // 调整游戏尺寸
      await sleep(300);
      game.value.scale.resize(newWidth, newHeight);

      // 发送尺寸变化事件给游戏场景
      EventBus.emit("resize", { width: newWidth, height: newHeight });
    }
  },
  { debounce: 100, immediate: true, flush: "post" }
);

onUnmounted(() => {
  if (game.value) {
    game.value.destroy(true);
    game.value = null;
    scene.value = null;
  }
});

defineExpose({ scene, game });

const selectedSprites = computed<MateSprite[]>(
  () => houseStore.houseSession?.mateSprites ?? []
);

watch(selectedSprites, (newVal, oldVal) => {
  if (!game.value || !scene.value) return;
  const toAddSprites: MateSprite[] = [];
  const toRemoveSprites: MateSprite[] = [];
  for (const newSprite of newVal) {
    if (!oldVal.some((oldSprite) => oldSprite.id === newSprite.id)) {
      toAddSprites.push(newSprite);
    }
  }
  for (const oldSprite of oldVal) {
    if (!newVal.some((newSprite) => newSprite.id === oldSprite.id)) {
      toRemoveSprites.push(oldSprite);
    }
  }
  // TODO 与{@code HouseScene}强耦合的逻辑应该放在useXXPhase中
  if (isControlMode.value) {
    scene.value.onMateEnter(toAddSprites, true);
    scene.value.onMateLeave(toRemoveSprites);
  }
});

/**
 * 触发思考状态
 */
watch(
  () => houseStore.mateId2HeadMsg,
  (
    newVal: Record<string, MateHouseSessionMessage | null>,
    oldVal: Record<string, MateHouseSessionMessage | null>
  ) => {
    const changedRecords: Record<string, MateHouseSessionMessage> = {};

    // 比较每个消息第一条是否相同，过滤出不同的消息
    Object.entries(newVal).forEach(
      ([mateId, message]: [string, MateHouseSessionMessage | null]) => {
        if (!message) return;
        const oldFirstMessage = oldVal[mateId];
        if (message.id !== oldFirstMessage?.id) {
          changedRecords[mateId] = message;
        }
      }
    );
    if (
      showCharacterDetail.value &&
      targetInfo.value?.mateId &&
      changedRecords[targetInfo.value.mateId]
    ) {
      //  详情面板打开时，若diffRecords包含targetInfo.mateId，则主动刷mateMesasgeList
      houseStore.getNewerMateMessageList(targetInfo.value.mateId);
    }
    // 进入思考状态，5秒后恢复闲逛状态
    Object.entries(changedRecords).forEach(([mateId, record]) => {
      const diffMinutes = moment().diff(moment(record.dtCreate), "minutes");
      if (diffMinutes > 2) {
        // ignore: old message
        return;
      }
      const spriteId = houseStore.mateId2SpriteId[mateId];
      // TODO 与{@code HouseScene}强耦合的逻辑应该放在useXXPhase中
      if (!spriteId || !isControlMode.value) return;
      scene.value?.enterThinking(spriteId.toString());
      // 提前缓存，避免被删除后无法显示
      const content = houseStore.mateId2HeadMsg[mateId]?.content ?? "";
      setTimeout(() => {
        if (!spriteId || !isControlMode.value) return;
        scene.value?.showTextBubble(spriteId.toString(), content);
        scene.value?.enterWondering(spriteId.toString());
      }, 5000);
    });
  },
  {
    deep: true,
  }
);

const targetInfo = ref<{
  mateId: number;
  spriteId: number;
} | null>(null);

const onSpriteClick = async (spriteId: number) => {
  scene.value?.toggleCamera(true, spriteId.toString());
  // 全屏模式下不显示弹窗
  if (gameUIStore.frameMode !== FrameMode.FULL_SCREEN_WITH_CONTROL) {
    showDetailPopup(spriteId);
  }
};

const showDetailPopup = async (spriteId: number) => {
  targetInfo.value = {
    spriteId: Number(houseSceneStore.followingCharId),
    mateId: houseStore.spriteId2MateId[spriteId],
  };
  await nextTick();
  showCharacterDetail.value = true;
};

watch(
  () => houseSceneStore.followingCharId,
  async () => {
    if (!houseSceneStore.followingCharId) return;

    // 全屏模式下不自动显示弹窗
    if (gameUIStore.frameMode !== FrameMode.FULL_SCREEN_WITH_CONTROL) {
      showDetailPopup(Number(houseSceneStore.followingCharId));
    }
  }
);

watch(showCharacterDetail, () => {
  if (
    showCharacterDetail.value ||
    !houseSceneStore.followingCharId ||
    gameUIStore.frameMode !== FrameMode.FULL_SCREEN_WITH_CONTROL
  ) {
    return;
  }
  scene.value?.toggleCamera(true, houseSceneStore.followingCharId, false);
});

const showInvitePopup = ref(false);
const showViewPopup = ref(false);
watch(
  routeMeta,
  (newVal) => {
    showViewPopup.value = !!newVal?.openPopup;
  },
  { immediate: true }
);
const { height: windowHeight } = useWindowSize();
provide(
  "maxHeight",
  computed(() => windowHeight.value * 0.9)
);
const uiStore = useUI();
watch(
  [
    showViewPopup,
    () => uiStore.showMateSelector,
    () => houseStore.showSpriteSelector,
    () => modalStore.showSelectModelModal,
    () => modalStore.showTaskPageModal,
    () => houseStore.showEditSubjectPopup,
    () => houseStore.showEventSpriteSelectorPopup,
    () => houseStore.showEventEndingDetailPopup,
    () => houseStore.showHouseSetting,
  ],
  (popupShowStates) => {
    if (!game.value?.input) return;
    game.value.input.enabled = !popupShowStates.some(Boolean);
  },
  {
    immediate: true,
  }
);
watch(
  () => gameUIStore.frameMode,
  async (newVal) => {
    if (newVal === FrameMode.WITH_PANEL && route.query.forceHideBottomNav) {
      // forceHideBottomNav
      router.replace({
        query: {
          ...route.query,
          forceHideBottomNav: undefined,
        },
      });
    } else if (
      newVal !== FrameMode.WITH_PANEL &&
      !route.query?.forceHideBottomNav
    ) {
      // forceHideBottomNav
      router.replace({
        query: {
          ...route.query,
          forceHideBottomNav: "true",
        },
      });
    }
  },
  { immediate: true }
);

watch(
  () => userStore.user.defaultHouseMapId,
  async () => {
    if (!(scene.value instanceof HouseScene) || !game.value) return;
    console.log("house map is going to change.");
    // 重新加载地图 JSON，再重启场景，让 BaseHouseScene.init() 使用最新的 roomJson
    await houseSceneStore.loadRoomJson();
    scene.value.scene.restart();
  },
  { flush: "post" }
);
</script>

<template>
  <div
    class="grid h-full w-full overflow-hidden relative"
    :class="{
      'grid-rows-2': gameUIStore.frameMode === FrameMode.WITH_PANEL,
      'grid-rows-1': gameUIStore.frameMode === FrameMode.FULL_SCREEN,
    }"
  >
    <div
      v-if="!loading"
      ref="containerRef"
      id="game-container"
      class="game-container w-full h-full overflow-hidden aspect-square relative"
    >
      <GameUI
        v-if="scene"
        class="absolute inset-0 pointer-events-none"
        :scene="scene!"
        @show-invite-popup="showInvitePopup = true"
      />
    </div>
    <div v-else class="w-full h-full flex items-center justify-center">
      <div class="loading loading-spinner loading-lg"></div>
    </div>
    <div
      v-if="gameUIStore.frameMode === FrameMode.WITH_PANEL"
      class="w-full h-full overflow-hidden"
    >
      <HousePanel
        :game="game!"
        :scene="scene!"
        :loading="loading"
        @sprite-click="onSpriteClick"
      />
    </div>

    <VanPopup
      v-model:show="showViewPopup"
      class="overflow-hidden h-[95%] w-full"
      position="bottom"
      @click.stop
      :close-on-click-overlay="false"
    >
      <div class="h-full w-full overflow-hidden">
        <router-view />
      </div>
    </VanPopup>

    <CharacterDetailPopup
      v-if="!loading && scene && targetInfo?.spriteId"
      v-model:show="showCharacterDetail"
      :mate-id="targetInfo.mateId"
      :sprite-id="targetInfo.spriteId"
      :scene="scene!"
    />
    <CharacterInvitePopup v-model:show="showInvitePopup" />
  </div>
</template>

<style scoped>
.game-container {
  width: 100%;
  height: 100%;
  position: relative;
}
</style>
